import { prependBayerStylesheetIfNeeded } from './bayer-vocabulary'
import { restoreWordMsoFieldCodeSpans } from './cross-reference-links-html'
import { normalizeAssistantHtml } from './normalize-assistant-html'

/**
 * What: Transforms LLM HTML so Word's insertHtml() renders it faithfully.
 * Why:  The Bayer stylesheet is prepended when Bayer Named Style classes are
 *       present (Word uses it as diff baseline).
 * How:  Shared normalisation first (bullets, vocab gate), then a single DOM
 *       parse for insert-specific transforms (list semantics, boundary para).
 *       Cross-reference link spans are restored after DOM serialize (see
 *       restoreWordMsoFieldCodeSpans).
 */
export function sanitizeForInsert(html: string): string {
  if (!html) return html

  const normalized = normalizeAssistantHtml(html, { skipBayerStyledLists: true })

  const template = document.createElement('template')
  template.innerHTML = normalized
  const root = template.content

  // LLM CSS can force li to display:block — restore list-item semantics
  root.querySelectorAll('li').forEach((li) => {
    if (li instanceof HTMLElement) {
      li.style.removeProperty('display')
      li.style.setProperty('display', 'list-item')
    }
  })

  const lastElement = root.lastElementChild
  const endsWithList =
    lastElement instanceof HTMLUListElement || lastElement instanceof HTMLOListElement
  if (endsWithList) {
    // Word insertHtml() can render terminal list tails as plain paragraphs.
    // Add a lightweight boundary paragraph to stabilize list semantics.
    const boundaryParagraph = document.createElement('p')
    boundaryParagraph.textContent = '\u200B'
    root.appendChild(boundaryParagraph)
  }

  const serializer = document.createElement('div')
  serializer.appendChild(root)
  // DOM encoding broke mso-field-code quotes; restore for Word REF links.
  const serialized = restoreWordMsoFieldCodeSpans(serializer.innerHTML)
  return prependBayerStylesheetIfNeeded(serialized)
}
